import { UserRole } from '@app/features/users/user.model';

export type AnomalyStatus = 'IN_PROGRESS' | 'RESOLVED';

/** Le choix de l'utilisateur sur un bandeau, mémorisé par l'API (décision 2026-027/06). */
export type AnomalyDisplayState = 'EXPANDED' | 'COLLAPSED' | 'CLOSED';

/** Où en est une anomalie de sa période d'affichage, à l'heure du serveur. */
export type AnomalyPhase = 'SCHEDULED' | 'DISPLAYED' | 'ENDED';

/**
 * Une anomalie telle que la direction la tient à jour. Les dates sont à l'heure de Paris, sans
 * décalage : `2026-10-23`, `12:00:00`, `2026-10-23T12:00:00`.
 */
export interface Anomaly {
  id: string;
  title: string;
  /** Markdown brut, rendu sans HTML (RG-anomalie-markdown-sans-html). */
  content: string;
  status: AnomalyStatus;
  targetRoles: UserRole[];
  /** `null` : affichée dès l'enregistrement. */
  startDate: string | null;
  /** `null` : le début vaut minuit qui ouvre le jour. */
  startTime: string | null;
  /** `null` : affichée tant qu'elle est en cours, puis deux jours après sa résolution. */
  endDate: string | null;
  /** `null` : la fin vaut minuit qui clôt le jour. */
  endTime: string | null;
  /** Début effectif de l'affichage, calculé par l'API. */
  displayedFrom: string;
  /** Fin effective de l'affichage (exclue), calculée par l'API ; `null` tant qu'elle est sans limite. */
  displayedUntil: string | null;
  phase: AnomalyPhase;
  archived: boolean;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  archivedBy: string | null;
  archivedAt: string | null;
}

/**
 * Le bandeau d'une anomalie qui concerne l'utilisateur maintenant. L'API les rend triées comme la
 * pile, avec l'état effectif de chacun : le choix de l'utilisateur, ou l'état par défaut de sa place.
 */
export interface VisibleAnomaly {
  id: string;
  title: string;
  content: string;
  status: AnomalyStatus;
  displayedFrom: string;
  displayedUntil: string | null;
  resolvedAt: string | null;
  updatedAt: string | null;
  displayState: AnomalyDisplayState;
}

/** Ce qu'un bandeau affiche : de quoi le dessiner, pour une anomalie visible comme pour l'aperçu. */
export type BannerAnomaly = Pick<
  VisibleAnomaly,
  'title' | 'content' | 'status' | 'displayedFrom' | 'resolvedAt' | 'updatedAt'
>;

export interface AnomalyRequest {
  title: string;
  content: string;
  status: AnomalyStatus;
  targetRoles: UserRole[];
  /** `YYYY-MM-DD` */
  startDate: string | null;
  /** `HH:mm` */
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
}

/** Seule la direction saisit les anomalies (RG-anomalie-saisie-par-la-direction). */
export const ANOMALY_AUTHORS: UserRole[] = ['directeur-general', 'directeur-rh'];

/** Durée d'affichage d'une anomalie résolue sans date de fin (décision 2026-027/04). */
export const RESOLVED_DISPLAY_DAYS = 2;

/** Rythme de relecture des anomalies (décision 2026-027/08). */
export const ANOMALY_REFRESH_MS = 60_000;
